import React, { useEffect, useMemo, useState } from 'react';
import { gql } from '@apollo/client';
import {
  Button, Card, Checkbox, Input, InputNumber, Select, Space, Table, Typography, message,
} from 'antd';
import { DeleteOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons';
import Page from 'components/Page';
import { useMutation, useQuery } from 'hooks/apollo';
import { PII_SCAN_SETTINGS_KEY } from 'shared/constants/piiScan';
import {
  getConfiguredModelTypes,
  getDefaultPiiScanSettings,
  getMaskCategories,
  normalizePiiScanSettings,
} from 'shared/utils/piiScan';
import PiiScanTestCard from './PiiScanTestCard';

const SETTINGS_QUERY = gql`
  query {
    settings(keys: ["piiScanSettings"])
  }
`;

const UPDATE_SETTINGS = gql`
  mutation($input: SettingsInput!) {
    updateSettings(input: $input) {
      id
      key
      value
    }
  }
`;

const PiiScanSettings = () => {
  const { data, loading, refetch } = useQuery(SETTINGS_QUERY);
  const [updateSettings, { loading: saving }] = useMutation(UPDATE_SETTINGS);
  const [settings, setSettings] = useState(getDefaultPiiScanSettings);
  const [newType, setNewType] = useState('');
  const [newCategory, setNewCategory] = useState('');

  useEffect(() => {
    const stored = data?.settings?.[PII_SCAN_SETTINGS_KEY];
    if (stored) {
      setSettings(normalizePiiScanSettings(stored));
    }
  }, [data]);

  const configuredTypes = useMemo(
    () => getConfiguredModelTypes(settings),
    [settings],
  );

  const modelTypeOptions = useMemo(
    () => configuredTypes.map((type) => ({ label: type, value: type })),
    [configuredTypes],
  );

  const maskCategories = useMemo(
    () => getMaskCategories(settings),
    [settings],
  );

  const rows = useMemo(
    () => configuredTypes.map((type) => ({
      key: type,
      type,
      category: settings.categoryMap[type] || 'number',
      enabled: settings.enabledTypes.includes(type),
      requires: settings.combinationRules[type] || [],
      minConfidence: Math.round((settings.confidenceThresholds[type] ?? 0) * 100),
    })),
    [configuredTypes, settings],
  );

  const onToggleType = (type, checked) => {
    setSettings((prev) => ({
      ...prev,
      enabledTypes: checked
        ? [...new Set([...prev.enabledTypes, type])]
        : prev.enabledTypes.filter((t) => t !== type),
    }));
  };

  const onCategoryChange = (type, category) => {
    setSettings((prev) => ({
      ...prev,
      categoryMap: { ...prev.categoryMap, [type]: category },
    }));
  };

  const onCombinationRequiresChange = (type, requires) => {
    setSettings((prev) => {
      const combinationRules = { ...prev.combinationRules };
      if (requires.length) {
        combinationRules[type] = requires;
      } else {
        delete combinationRules[type];
      }
      return { ...prev, combinationRules };
    });
  };

  const onConfidenceThresholdChange = (type, percent) => {
    const value = percent == null ? 0 : percent / 100;
    setSettings((prev) => ({
      ...prev,
      confidenceThresholds: { ...prev.confidenceThresholds, [type]: value },
    }));
  };

  const onAddType = () => {
    const type = newType.trim();
    if (!type) {
      message.warning('Enter a model type');
      return;
    }
    if (settings.categoryMap[type]) {
      message.warning('Type already exists');
      return;
    }
    const category = newCategory.trim() || 'number';
    setSettings((prev) => ({
      ...prev,
      categoryMap: { ...prev.categoryMap, [type]: category },
      confidenceThresholds: { ...prev.confidenceThresholds, [type]: 0 },
      enabledTypes: [...new Set([...prev.enabledTypes, type])],
    }));
    setNewType('');
    setNewCategory('');
  };

  const onRemoveType = (type) => {
    setSettings((prev) => {
      const categoryMap = { ...prev.categoryMap };
      delete categoryMap[type];
      const combinationRules = { ...prev.combinationRules };
      delete combinationRules[type];
      const confidenceThresholds = { ...prev.confidenceThresholds };
      delete confidenceThresholds[type];
      return {
        ...prev,
        categoryMap,
        combinationRules,
        confidenceThresholds,
        enabledTypes: prev.enabledTypes.filter((t) => t !== type),
      };
    });
  };

  const onSave = async () => {
    await updateSettings({
      variables: {
        input: {
          settings: [{
            key: PII_SCAN_SETTINGS_KEY,
            value: JSON.stringify(settings),
          }],
        },
      },
    });
    message.success('PII scan settings saved');
    refetch();
  };

  const columns = [
    {
      dataIndex: 'type',
      title: 'Model type',
      width: 220,
      render: (type) => <Typography.Text code>{type}</Typography.Text>,
    },
    {
      dataIndex: 'category',
      title: 'Mask category',
      width: 160,
      render: (category, record) => (
        <>
          <Input
            value={category}
            onChange={(e) => onCategoryChange(record.type, e.target.value)}
            list={`mask-categories-${record.type}`}
          />
          <datalist id={`mask-categories-${record.type}`}>
            {maskCategories.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
        </>
      ),
    },
    {
      dataIndex: 'enabled',
      title: 'Detect',
      width: 72,
      align: 'center',
      render: (enabled, record) => (
        <Checkbox
          checked={enabled}
          onChange={(e) => onToggleType(record.type, e.target.checked)}
        />
      ),
    },
    {
      dataIndex: 'minConfidence',
      title: 'Min confidence',
      width: 120,
      render: (minConfidence, record) => (
        <InputNumber
          min={0}
          max={100}
          step={1}
          value={minConfidence}
          formatter={(value) => `${value}%`}
          parser={(value) => value.replace('%', '')}
          onChange={(value) => onConfidenceThresholdChange(record.type, value)}
        />
      ),
    },
    {
      dataIndex: 'requires',
      title: 'Only when also present',
      render: (requires, record) => (
        <Select
          mode="multiple"
          allowClear
          style={{ width: '100%' }}
          placeholder="Always when detected"
          value={requires}
          options={modelTypeOptions.filter((o) => o.value !== record.type)}
          onChange={(value) => onCombinationRequiresChange(record.type, value)}
        />
      ),
    },
    {
      key: 'actions',
      width: 48,
      render: (_, record) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => onRemoveType(record.type)}
        />
      ),
    },
  ];

  return (
    <Page loading={loading}>
      <Page.Header
        title="PII scan settings"
        extra={(
          <Button type="primary" icon={<SaveOutlined />} onClick={onSave} loading={saving}>
            Save
          </Button>
        )}
      />
      <Typography.Paragraph type="secondary">
        Configure model types, mask categories, detection, combination rules, and minimum
        confidence. Types and mask categories are fully defined here and used by scanning
        and redaction.
      </Typography.Paragraph>
      <Card size="small">
        <Table
          dataSource={rows}
          columns={columns}
          pagination={false}
          size="small"
          scroll={{ y: 560 }}
        />
        <Space style={{ marginTop: 12 }}>
          <Input
            placeholder="Model type"
            value={newType}
            onChange={(e) => setNewType(e.target.value)}
            style={{ width: 220 }}
          />
          <Input
            placeholder="Mask category"
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            list="mask-categories-new"
            style={{ width: 160 }}
          />
          <datalist id="mask-categories-new">
            {maskCategories.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
          <Button icon={<PlusOutlined />} onClick={onAddType}>
            Add type
          </Button>
        </Space>
      </Card>
      <Card title="Test scan" size="small" style={{ marginTop: 16 }}>
        <PiiScanTestCard settings={settings} />
      </Card>
    </Page>
  );
};

export default PiiScanSettings;
